import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, UserRole, CorporateInfo, LoginCredentials, RegisterData, OtpRequest, OtpVerify, MFALoginData } from '../types';
import authService from '../services/authService';

interface MFAState {
  required: boolean;
  userId: number | null;
  mfaToken: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mfaState: MFAState;
  // Role helpers
  isSuperAdmin: boolean;
  isCorporateStaff: boolean;
  isEmployee: boolean;
  isCustomerService: boolean;
  userRole: UserRole | null;
  corporateInfo: CorporateInfo | null;
  // Methods
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithOtp: (data: OtpVerify) => Promise<void>;
  verifyMFA: (data: MFALoginData) => Promise<void>;
  requestOtp: (data: OtpRequest) => Promise<string>;
  register: (data: RegisterData) => Promise<string>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  clearError: () => void;
  cancelMFA: () => void;
  /** Store a successful auth response (e.g. after converting to an individual). */
  completeLogin: (response: { access: string; refresh: string; user: User }) => void;
  isIndividual: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Sign-in answered "your employer deactivated you"; carries the conversion offer. */
export interface DeactivationOffer {
  code: 'account_deactivated';
  message: string;
  conversion_token: string;
  corporate_name?: string;
  name?: string;
  username?: string;
  personal_email?: string;
}

export class AccountDeactivatedError extends Error {
  offer: DeactivationOffer;

  constructor(offer: DeactivationOffer) {
    super(offer.message);
    this.name = 'AccountDeactivatedError';
    this.offer = offer;
  }
}

/**
 * Individuals get a stand-in corporate from the API (for the released mobile
 * app); the web keys off account_type instead, so drop it.
 */
export const normalizeUser = (u: User): User =>
  u && u.account_type === 'individual' ? { ...u, corporate: null, corporate_info: null } : u;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaState, setMfaState] = useState<MFAState>({ required: false, userId: null, mfaToken: null });

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const accessToken = localStorage.getItem('accessToken');

        if (storedUser && accessToken) {
          // Verify token is still valid by fetching profile
          try {
            const profile = normalizeUser(await authService.getProfile());
            setUser(profile);
            localStorage.setItem('user', JSON.stringify(profile));
          } catch (err) {
            // Token is invalid, clear storage
            console.error('Token validation failed:', err);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setError(null);
    setIsLoading(true);
    setMfaState({ required: false, userId: null, mfaToken: null });

    try {
      const response = await authService.login(credentials);

      // Check if MFA is required
      if (response.mfa_required && response.user_id) {
        setMfaState({ required: true, userId: response.user_id, mfaToken: response.mfa_token || null });
        return;
      }

      // Store tokens
      const signedIn = normalizeUser(response.user);
      localStorage.setItem('accessToken', response.access);
      localStorage.setItem('refreshToken', response.refresh);
      localStorage.setItem('user', JSON.stringify(signedIn));

      setUser(signedIn);
    } catch (err: any) {
      if (err.response?.data?.code === 'account_deactivated') {
        throw new AccountDeactivatedError(err.response.data as DeactivationOffer);
      }
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyMFA = useCallback(async (data: MFALoginData) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.verifyMFA(data);

      const signedIn = normalizeUser(response.user);
      localStorage.setItem('accessToken', response.access);
      localStorage.setItem('refreshToken', response.refresh);
      localStorage.setItem('user', JSON.stringify(signedIn));

      setUser(signedIn);
      setMfaState({ required: false, userId: null, mfaToken: null });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        'Invalid MFA code. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requestOtp = useCallback(async (data: OtpRequest): Promise<string> => {
    setError(null);

    try {
      const response = await authService.requestOtp(data);
      return response.message;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.response?.data?.identifier?.[0] ||
        err.response?.data?.non_field_errors?.[0] ||
        'Failed to send OTP. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const loginWithOtp = useCallback(async (data: OtpVerify) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.verifyOtp(data);

      // Store tokens
      const signedIn = normalizeUser(response.user);
      localStorage.setItem('accessToken', response.access);
      localStorage.setItem('refreshToken', response.refresh);
      localStorage.setItem('user', JSON.stringify(signedIn));

      setUser(signedIn);
    } catch (err: any) {
      if (err.response?.data?.code === 'account_deactivated') {
        throw new AccountDeactivatedError(err.response.data as DeactivationOffer);
      }
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.identifier?.[0] ||
        err.response?.data?.otp?.[0] ||
        'Invalid OTP. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<string> => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authService.register(data);
      return response.message;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.email?.[0] ||
        err.response?.data?.mobile_number?.[0] ||
        err.response?.data?.password?.[0] ||
        'Registration failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);

    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (err) {
      console.error('Logout error:', err);
      // Continue with logout even if API call fails
    } finally {
      // Clear all stored data
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('activeProfile');
      localStorage.removeItem('sr_accessToken');
      localStorage.removeItem('sr_refreshToken');
      setUser(null);
      setError(null);
      setIsLoading(false);
    }
  }, []);

  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const cancelMFA = useCallback(() => {
    setMfaState({ required: false, userId: null, mfaToken: null });
    setError(null);
  }, []);

  const isSuperAdmin = user?.role === 'super_admin';
  const isCorporateStaff = user?.role === 'corporate_admin' || user?.role === 'corporate_hr';
  const isEmployee = user?.role === 'employee';
  const isCustomerService = user?.role === 'customer_service';
  const userRole = user?.role || null;
  const corporateInfo = user?.corporate || user?.corporate_info || null;
  const isIndividual = user?.account_type === 'individual';

  const completeLogin = useCallback((response: { access: string; refresh: string; user: User }) => {
    const signedIn = normalizeUser(response.user);
    localStorage.setItem('accessToken', response.access);
    localStorage.setItem('refreshToken', response.refresh);
    localStorage.setItem('user', JSON.stringify(signedIn));
    setUser(signedIn);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    mfaState,
    isSuperAdmin,
    isCorporateStaff,
    isEmployee,
    isCustomerService,
    userRole,
    corporateInfo,
    completeLogin,
    isIndividual,
    login,
    loginWithOtp,
    verifyMFA,
    requestOtp,
    register,
    logout,
    updateUser,
    clearError,
    cancelMFA,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
